import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BiHide } from 'react-icons/bi';
import { FaPlus } from 'react-icons/fa';
import { LuArrowDown, LuArrowUp } from 'react-icons/lu';

import { useQueryClient } from '@tanstack/react-query';
import { Alert, Checkbox, Modal, Segmented, Spin } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import Button from 'components/common/Button';
import AddNewButton from 'components/common/Button/AddNewButton';
import LinkButton from 'components/common/LinkButton/LinkButton';
import SearchInput from 'components/common/SearchInput/SearchInput';
import { ShortcutKey } from 'components/common/ShortcutHint/ShortcutHint';
import {
  FARM_NAME_REQUIRED_MESSAGE,
  FARM_SAVE_ERROR_MESSAGE,
  PADDOCK_NAME_REQUIRED_MESSAGE,
  PADDOCK_SAVE_ERROR_MESSAGE,
  PADDOCKS_BULK_ERROR_MESSAGE,
  PADDOCKS_DELETE_FARMS_NOTE,
  PADDOCKS_DELETE_PLANS_WARNING,
  PADDOCKS_DELETED_MESSAGE,
  PADDOCKS_EXCLUDED_MESSAGE,
  PADDOCKS_INCLUDE_SKIPPED_MESSAGE,
  PADDOCKS_LOAD_ERROR_MESSAGE,
  PADDOCKS_INCLUDED_MESSAGE,
  PADDOCKS_NO_FARMS_MESSAGE,
  PADDOCKS_NO_MATCHES_MESSAGE,
} from 'constants/messageConstants';
import { ZONES_QUERY_KEY } from 'constants/queryKeys';
import { ORG_KEY_PREFIX } from 'constants/routeConstants';
import {
  AREA_UNIT,
  PADDOCKS_ADD_FARM_LABEL,
  PADDOCKS_ADD_PADDOCK_LABEL,
  PADDOCKS_ARABLE_AREA_LABEL,
  PADDOCKS_AREA_LABEL,
  PADDOCKS_CANCEL_LABEL,
  PADDOCKS_CLEAR_SELECTION_LABEL,
  PADDOCKS_DELETE_LABEL,
  PADDOCKS_DELETE_TITLE,
  PADDOCKS_EXCLUDE_LABEL,
  PADDOCKS_EXCLUDED_LABEL,
  PADDOCKS_FARM_NAME_PLACEHOLDER,
  PADDOCKS_FIELD_LABEL,
  PADDOCKS_FILTER_ALL_LABEL,
  PADDOCKS_FILTER_EXCLUDED_LABEL,
  PADDOCKS_FILTER_INCLUDED_LABEL,
  PADDOCKS_GRID_LABEL,
  PADDOCKS_INCLUDE_LABEL,
  PADDOCKS_MORE_COUNT,
  PADDOCKS_NAME_COLUMN_LABEL,
  PADDOCKS_NOTES_LABEL,
  PADDOCKS_PADDOCK_NAME_PLACEHOLDER,
  PADDOCKS_RANGE_HINT_AFTER,
  PADDOCKS_RANGE_HINT_BEFORE,
  PADDOCKS_RANGE_HINT_KEY,
  PADDOCKS_SEARCH_PLACEHOLDER,
  PADDOCKS_SELECT_ALL_LABEL,
  PADDOCKS_SCOPED_NOTICE,
  PADDOCKS_SELECT_HINT,
  PADDOCKS_SELECTED_IN_TREE_LABEL,
  PADDOCKS_SHOW_ALL_FARMS_LABEL,
  PADDOCKS_SORT_TOOLTIP,
  PADDOCKS_SELECT_ROW_LABEL,
  PADDOCKS_SELECTED_COUNT,
  PADDOCKS_STATUS_FILTER_LABEL,
  PADDOCKS_UNDO_EXCLUDE,
  PADDOCKS_UNDO_FIELD,
  PADDOCKS_UNDO_INCLUDE,
  PADDOCKS_UNIT_LABEL,
} from 'constants/uiConstants';
import { readCachedFarm } from 'hooks/farms/farmsCache';
import useDeleteFarm from 'hooks/farms/useDeleteFarm';
import useDeletePaddock from 'hooks/farms/useDeletePaddock';
import useUpsertFarm from 'hooks/farms/useUpsertFarm';
import useUpsertPaddock from 'hooks/farms/useUpsertPaddock';
import { useFetchInputsByOrgId } from 'hooks/inputs/useFetchInputsByOrgId';
import { useFetchFarmsByOrgId } from 'hooks/useFetchFarmsByOrgId';
import { useFetchGAreaUnits } from 'hooks/useFetchGAreaUnits';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import type { IFarm } from 'interfaces/IFarm';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { InputType, type IUnit } from 'interfaces/IInput';
import type { IPaddock } from 'interfaces/IPaddock';
import type { IZone } from 'interfaces/IZone';
import {
  buildFarmExcluded,
  buildFarmExclusion,
  buildNewFarm,
  buildNewPaddock,
  buildPaddocksExcluded,
  readFarmExclusion,
  type FarmExclusionState,
  type FarmFormValues,
  type PaddockFormValues,
} from 'utils/farmPayloads';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import { indexInputsByPaddock } from 'utils/FarmUtils';

import type { InsightsScopeLevel } from '../Insights/insightUtils';
import AreaUnitSelect from './AreaUnitSelect';
import DraftRow, { type DraftRowValues } from './DraftRow';
import { GridNumberInput, GridTextInput } from './GridCellInputs';
import {
  buildPaddockGridRows,
  type PaddockGridSort,
  type PaddockSortKey,
  type PaddockStatusFilter,
} from './paddockGridRows';
import PlanningUndoNote from '../PlanningUndoNote';
import WorkspaceEmptyState from '../WorkspaceEmptyState';
import PaddocksContextMenu from './PaddocksContextMenu';
import usePaddocksAutosave from './usePaddocksAutosave';
import usePaddocksUndo from './usePaddocksUndo';

const EMPTY_G_UNITS: GUnit[] = [];
const NOTE_DURATION_MS = 5000;
const DELETE_LIST_MAX = 8;
const MODAL_FROM_CENTRE = { x: 0, y: 0 };

const farmKey = (farmId: string) => `farm:${farmId}`;
const paddockKey = (paddockId: string) => `paddock:${paddockId}`;

// Horizontal lines only, at gray-medium — the lightest grey that meets 3:1
// for lines. A heavier rule above each farm separates the farms.
const CELL = 'border-b border-gray-medium p-0 align-middle';
const FARM_CELL = `${CELL} border-t-2 border-t-gray-dark`;
// Headings stay in view while the rows scroll, as in Rotation and Analysis.
const HEAD_CELL =
  'sticky top-0 z-10 border-b-2 border-gray-dark bg-white px-2 py-2 text-sm font-semibold text-gray-dark';

interface PaddocksViewProps {
  orgId: string | null;
  gOrgId: string | null;
  scopeLevel: InsightsScopeLevel | undefined;
  /** The tree's farm — for a paddock or plan, the farm it belongs to. */
  selectedFarmId: string | null;
  /** The tree's paddock, marked in the grid; null at farm or org level. */
  selectedPaddockId: string | null;
  /**
   * Save state for the workspace's status label (SavingStatusLabel), which
   * shows it the way Planning and Nutrition do; null once the lens closes.
   */
  onSaveStatusChange: (status: PaddocksSaveStatus | null) => void;
}

/** What the workspace's status label shows for the Paddocks lens. */
export interface PaddocksSaveStatus {
  isSaving: boolean;
  /** Staged edits waiting for the autosave. */
  unsavedCount: number;
  hasSaved: boolean;
  errorMessage: string | null;
  /** Saves staged edits now — Save, and Retry after an error. */
  save: () => void;
}

type Adding = { kind: 'farm' } | { kind: 'paddock'; farmId: string } | null;
type Note = { text: string; isError: boolean } | null;

/**
 * The Planning Paddocks lens (OP-2481) — a grid of farms and their paddocks,
 * laid out like the Rotation planner. Names, areas and units are edited in
 * place; ticking rows offers Exclude, Include and Delete for all of them at
 * once.
 *
 * Reads the raw farms query, not useVisibleOrgData, so excluded farms and
 * paddocks stay listed here and can be re-included; everywhere else hides
 * them. The organisation lists every farm; a farm lists just itself.
 *
 * Writes run one at a time and each builds its payload from the latest
 * cached farm when it starts — a farm save sends every paddock, so two
 * quick edits built from the same copy would undo each other.
 */
const PaddocksView: React.FC<PaddocksViewProps> = ({
  orgId,
  gOrgId,
  scopeLevel,
  selectedFarmId,
  selectedPaddockId,
  onSaveStatusChange,
}) => {
  const queryClient = useQueryClient();
  const farmsQuery = useFetchFarmsByOrgId(orgId);
  const { data: inputs } = useFetchInputsByOrgId(orgId, InputType.PlanActual);
  const { data: gUnits = EMPTY_G_UNITS } = useFetchGAreaUnits(gOrgId);
  const { data: userProfile } = useFetchUserProfile();

  const { mutateAsync: upsertFarm } = useUpsertFarm(orgId);
  const { mutateAsync: deleteFarm } = useDeleteFarm(orgId);
  const { mutateAsync: upsertPaddock } = useUpsertPaddock(orgId);
  const { mutateAsync: deletePaddock } = useDeletePaddock(orgId);

  const [selection, setSelection] = useState<Set<string>>(() => new Set());
  const [adding, setAdding] = useState<Adding>(null);
  // Bulk results ("3 rows excluded") beside the action bar; save state goes
  // to the workspace's status label instead.
  const [note, setNote] = useState<Note>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  const markSaved = useCallback(() => {
    setSaveError(null);
    setHasSaved(true);
  }, []);
  const [pendingWrites, setPendingWrites] = useState(0);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // ── Write queue ──────────────────────────────────────────────────────────
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const enqueue = useCallback((job: () => Promise<void>) => {
    setPendingWrites((count) => count + 1);
    queueRef.current = queueRef.current
      .then(job)
      .finally(() => setPendingWrites((count) => count - 1));
  }, []);

  // Confirmations clear themselves; errors stay until the next action.
  useEffect(() => {
    if (!note || note.isError) return undefined;
    const timer = window.setTimeout(() => setNote(null), NOTE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [note]);

  const latestFarm = useCallback(
    (farmId: string) =>
      orgId ? readCachedFarm(queryClient, orgId, farmId) : undefined,
    [orgId, queryClient]
  );

  const defaultAreaUnit = useMemo<IUnit | null>(() => {
    const unit = gUnits.find((item) => item.code === AREA_UNIT);
    return unit ? { unit: unit.code, globalId: unit.globalId } : null;
  }, [gUnits]);

  // ── Rows ─────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaddockStatusFilter>('all');
  const [sort, setSort] = useState<PaddockGridSort>({
    key: 'name',
    direction: 'asc',
  });
  const isFiltered = !!search.trim() || statusFilter !== 'all';

  // The organisation lists every farm. A farm, paddock or plan in the tree
  // lists that one farm — a paddock's or plan's own farm — with the notice
  // above the grid saying so.
  const isOneFarm = scopeLevel !== 'org';
  const scopedFarms = useMemo(
    () =>
      isOneFarm
        ? (farmsQuery.data ?? []).filter((farm) => farm.id === selectedFarmId)
        : (farmsQuery.data ?? []),
    [farmsQuery.data, isOneFarm, selectedFarmId]
  );
  const navigateFarms = useFarmsLinkNavigate();

  // The rows on screen. Selection, bulk actions and select-all only ever
  // reach these, so a filtered-out row can't be changed by mistake.
  const rows = useMemo(
    () =>
      buildPaddockGridRows(scopedFarms, {
        search,
        status: statusFilter,
        sort,
      }),
    [scopedFarms, search, statusFilter, sort]
  );
  const farms = useMemo(() => rows.map((row) => row.farm), [rows]);
  const paddocksByFarm = useMemo(
    () => new Map(rows.map((row) => [row.farm.id, row.paddocks])),
    [rows]
  );

  const toggleSort = (key: PaddockSortKey) =>
    setSort((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  // Every key a row can have, so a selection never holds a deleted row.
  const allKeys = useMemo(
    () =>
      farms.flatMap((farm) => [
        farmKey(farm.id),
        ...(paddocksByFarm.get(farm.id) ?? []).map((p) =>
          paddockKey(p.paddockId)
        ),
      ]),
    [farms, paddocksByFarm]
  );
  const selected = useMemo(
    () => allKeys.filter((key) => selection.has(key)),
    [allKeys, selection]
  );

  // Shift+click ticks (or unticks) every row between the last box clicked
  // and this one, in screen order, to match the box clicked.
  const anchorRef = useRef<string | null>(null);
  const toggle = (key: string, isRange: boolean) => {
    const anchor = anchorRef.current;
    anchorRef.current = key;
    setSelection((prev) => {
      const next = new Set(prev);
      const tick = !prev.has(key);
      const from = anchor ? allKeys.indexOf(anchor) : -1;
      const to = allKeys.indexOf(key);
      const range =
        isRange && from >= 0 && to >= 0
          ? allKeys.slice(Math.min(from, to), Math.max(from, to) + 1)
          : [key];
      range.forEach((item) => (tick ? next.add(item) : next.delete(item)));
      return next;
    });
  };
  const isShiftClick = (event: CheckboxChangeEvent) =>
    (event.nativeEvent as MouseEvent).shiftKey;

  // ── Right-click menu ─────────────────────────────────────────────────────
  // Right-clicking a ticked row acts on everything ticked; an unticked row
  // is ticked on its own first, as in a file list. Highlighted text in a
  // field keeps the browser's own menu, for Copy.
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const openMenu = (event: React.MouseEvent, key: string) => {
    const target = event.target as HTMLElement;
    // Highlighted text in a field → keep the browser's own Copy menu.
    if (
      (target as HTMLInputElement).matches('input, textarea') &&
      (target as HTMLInputElement).selectionStart !==
        (target as HTMLInputElement).selectionEnd
    ) {
      return;
    }
    // Mic button → let VoiceMicButton's AntD Dropdown open the language picker;
    // suppress the row context menu entirely.
    if (target.closest('.paddocks-grid-voice')) {
      return;
    }
    event.preventDefault();
    if (!selection.has(key)) setSelection(new Set([key]));
    anchorRef.current = key;
    setMenu({ x: event.clientX, y: event.clientY });
  };
  const excludedByKey = useMemo(
    () =>
      new Map(
        rows.flatMap((row) => [
          [farmKey(row.farm.id), row.farm.isExcluded] as const,
          ...row.paddocks.map(
            (p) => [paddockKey(p.paddockId), p.isExcluded] as const
          ),
        ])
      ),
    [rows]
  );

  // A ticked farm covers its paddocks, so they aren't saved twice.
  const resolveTargets = () => {
    const farmIds = farms
      .filter((farm) => selection.has(farmKey(farm.id)))
      .map((farm) => farm.id);
    const paddockIdsByFarm = new Map<string, Set<string>>();
    farms.forEach((farm) => {
      if (farmIds.includes(farm.id)) return;
      const ids = (paddocksByFarm.get(farm.id) ?? [])
        .filter((p) => selection.has(paddockKey(p.paddockId)))
        .map((p) => p.paddockId);
      if (ids.length) paddockIdsByFarm.set(farm.id, new Set(ids));
    });
    return { farmIds, paddockIdsByFarm };
  };

  // ── Field edits — saved together, like Planning ──────────────────────────
  const {
    stageFarm,
    stagePaddock,
    flush,
    farmValues,
    paddockValues,
    stagedCount,
  } = usePaddocksAutosave({
    userProfile,
    enqueue,
    latestFarm,
    latestZone: (zoneId) =>
      (orgId &&
        queryClient
          .getQueryData<IZone[]>([ZONES_QUERY_KEY, orgId])
          ?.find((item) => item.id === zoneId)) ||
      null,
    upsertFarm,
    onSaved: (failed) =>
      failed > 0
        ? setSaveError(PADDOCKS_BULK_ERROR_MESSAGE(failed))
        : markSaved(),
  });

  // ── Undo ─────────────────────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);

  // Undo puts back each farm's recorded exclusion, saved straight away.
  const restoreExclusion = (
    states: ReadonlyMap<string, FarmExclusionState>
  ) => {
    flush();
    enqueue(async () => {
      let failed = 0;
      for (const [farmId, state] of Array.from(states)) {
        const farm = latestFarm(farmId);
        if (!farm) continue;
        try {
          await upsertFarm({
            farm: buildFarmExclusion(farm, state, userProfile),
          });
        } catch {
          failed += 1;
        }
      }
      if (failed > 0) setSaveError(PADDOCKS_BULK_ERROR_MESSAGE(failed));
      else markSaved();
    });
  };

  const { record: recordUndo, note: undoNote } = usePaddocksUndo({
    containerRef,
    stageFarm,
    stagePaddock,
    restoreExclusion,
  });

  // A grid edit, staged for autosave and recorded so undo can put it back.
  const editFarm = <K extends keyof FarmFormValues>(
    farm: IFarm,
    field: K,
    fieldLabel: string,
    value: FarmFormValues[K]
  ) => {
    const after = { [field]: value } as Partial<FarmFormValues>;
    recordUndo({
      kind: 'farmField',
      label: PADDOCKS_UNDO_FIELD(fieldLabel, farm.farmName),
      farmId: farm.id,
      before: { [field]: farmValues(farm)[field] } as Partial<FarmFormValues>,
      after,
    });
    stageFarm(farm.id, after);
  };

  const editPaddock = <K extends keyof PaddockFormValues>(
    farm: IFarm,
    paddock: IPaddock,
    field: K,
    fieldLabel: string,
    value: PaddockFormValues[K]
  ) => {
    const after = { [field]: value } as Partial<PaddockFormValues>;
    recordUndo({
      kind: 'paddockField',
      label: PADDOCKS_UNDO_FIELD(fieldLabel, paddock.paddockName),
      farmId: farm.id,
      paddockId: paddock.paddockId,
      before: {
        [field]: paddockValues(paddock)[field],
      } as Partial<PaddockFormValues>,
      after,
    });
    stagePaddock(farm.id, paddock.paddockId, after);
  };

  // The workspace's status label shows saving like Planning's: Save with the
  // unsaved count, Saving…, Saved, or Error with Retry.
  const saveNow = useCallback(() => {
    setSaveError(null);
    flush();
  }, [flush]);
  useEffect(() => {
    onSaveStatusChange({
      isSaving: pendingWrites > 0,
      unsavedCount: stagedCount,
      hasSaved,
      errorMessage: saveError,
      save: saveNow,
    });
  }, [
    onSaveStatusChange,
    pendingWrites,
    stagedCount,
    hasSaved,
    saveError,
    saveNow,
  ]);
  useEffect(() => () => onSaveStatusChange(null), [onSaveStatusChange]);

  // Closing the tab mid-save would lose the changes; the browser asks first.
  const hasUnsaved = stagedCount > 0 || pendingWrites > 0;
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  // ── Adding ───────────────────────────────────────────────────────────────
  // A new row saves as soon as it's finished — it isn't staged with edits.
  const addFarm = (values: DraftRowValues) => {
    setAdding(null);
    if (!orgId) return;
    enqueue(async () => {
      try {
        await upsertFarm({
          farm: buildNewFarm(
            orgId,
            {
              farmName: values.name,
              farmArea: values.area,
              areaUnit: values.areaUnit,
              notes: values.notes,
            },
            userProfile
          ),
        });
        markSaved();
      } catch {
        setSaveError(FARM_SAVE_ERROR_MESSAGE);
      }
    });
  };

  const addPaddock = (farmId: string, values: DraftRowValues) => {
    setAdding(null);
    if (!orgId) return;
    enqueue(async () => {
      if (!latestFarm(farmId)) return;
      try {
        await upsertPaddock({
          farmId,
          ...buildNewPaddock(
            orgId,
            {
              paddockName: values.name,
              paddockArea: values.area,
              arableArea: values.arableArea,
              areaUnit: values.areaUnit,
              notes: values.notes,
            },
            userProfile
          ),
        });
        markSaved();
      } catch {
        setSaveError(PADDOCK_SAVE_ERROR_MESSAGE);
      }
    });
  };

  // ── Bulk actions ─────────────────────────────────────────────────────────
  // Both save staged edits first, so they build on what's been typed.
  const setExcluded = (isExcluded: boolean) => {
    const { farmIds, paddockIdsByFarm } = resolveTargets();
    flush();
    setSelection(new Set());
    setNote(null);
    enqueue(async () => {
      let done = 0;
      let failed = 0;
      let skipped = 0;
      // Each saved farm's state before and after, for undo.
      const before = new Map<string, FarmExclusionState>();
      const after = new Map<string, FarmExclusionState>();
      for (const farmId of farmIds) {
        const farm = latestFarm(farmId);
        if (!farm) continue;
        const updated = buildFarmExcluded(farm, isExcluded, userProfile);
        try {
          await upsertFarm({ farm: updated });
          before.set(farmId, readFarmExclusion(farm));
          after.set(farmId, readFarmExclusion(updated));
          done += 1;
        } catch {
          failed += 1;
        }
      }
      for (const [farmId, paddockIds] of Array.from(paddockIdsByFarm)) {
        const farm = latestFarm(farmId);
        if (!farm) continue;
        // A paddock follows its excluded farm; it can't be included alone.
        if (!isExcluded && farm.isExcluded) {
          skipped += paddockIds.size;
          continue;
        }
        const updated = buildPaddocksExcluded(
          farm,
          paddockIds,
          isExcluded,
          userProfile
        );
        try {
          await upsertFarm({ farm: updated });
          before.set(farmId, readFarmExclusion(farm));
          after.set(farmId, readFarmExclusion(updated));
          done += paddockIds.size;
        } catch {
          failed += paddockIds.size;
        }
      }
      if (done > 0) {
        recordUndo({
          kind: 'exclusion',
          label: isExcluded
            ? PADDOCKS_UNDO_EXCLUDE(done)
            : PADDOCKS_UNDO_INCLUDE(done),
          before,
          after,
        });
      }
      if (failed > 0) {
        setSaveError(PADDOCKS_BULK_ERROR_MESSAGE(failed));
      } else if (done > 0) {
        markSaved();
      }
      if (skipped > 0) {
        setNote({
          text: PADDOCKS_INCLUDE_SKIPPED_MESSAGE(skipped),
          isError: true,
        });
      } else if (done > 0) {
        setNote({
          text: isExcluded
            ? PADDOCKS_EXCLUDED_MESSAGE(done)
            : PADDOCKS_INCLUDED_MESSAGE(done),
          isError: false,
        });
      }
    });
  };

  const confirmDelete = () => {
    const { farmIds, paddockIdsByFarm } = resolveTargets();
    flush();
    setIsDeleteOpen(false);
    setSelection(new Set());
    setNote(null);
    enqueue(async () => {
      let done = 0;
      let failed = 0;
      for (const farmId of farmIds) {
        try {
          await deleteFarm(farmId);
          done += 1;
        } catch {
          failed += 1;
        }
      }
      for (const [farmId, paddockIds] of Array.from(paddockIdsByFarm)) {
        for (const paddockId of Array.from(paddockIds)) {
          try {
            await deletePaddock({ farmId, paddockId });
            done += 1;
          } catch {
            failed += 1;
          }
        }
      }
      if (failed > 0) {
        setSaveError(PADDOCKS_BULK_ERROR_MESSAGE(failed));
      } else {
        markSaved();
        setNote({ text: PADDOCKS_DELETED_MESSAGE(done), isError: false });
      }
    });
  };

  // What the delete dialog lists, and how many plans stop showing.
  const summariseDelete = () => {
    const { farmIds, paddockIdsByFarm } = resolveTargets();
    const names: string[] = [];
    const paddockIds = new Set<string>();
    farms.forEach((farm) => {
      if (farmIds.includes(farm.id)) {
        names.push(farm.farmName);
        farm.paddocks.forEach((p) => paddockIds.add(p.paddockId));
      }
    });
    paddockIdsByFarm.forEach((ids, farmId) => {
      const farm = farms.find((item) => item.id === farmId);
      ids.forEach((id) => {
        paddockIds.add(id);
        const paddock = farm?.paddocks.find((p) => p.paddockId === id);
        if (paddock) names.push(paddock.paddockName);
      });
    });
    const plansByPaddock = indexInputsByPaddock(inputs ?? []);
    const planIds = new Set<string>();
    paddockIds.forEach((id) =>
      plansByPaddock.get(id)?.forEach((plan) => planIds.add(plan.id))
    );
    return {
      farmCount: farmIds.length,
      paddockCount: Array.from(paddockIdsByFarm.values()).reduce(
        (sum, ids) => sum + ids.size,
        0
      ),
      names,
      planCount: planIds.size,
    };
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (farmsQuery.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="small" />
      </div>
    );
  }

  if (farmsQuery.isError) {
    return (
      <Alert title={PADDOCKS_LOAD_ERROR_MESSAGE} type="error" showIcon />
    );
  }

  const allSelected = allKeys.length > 0 && selected.length === allKeys.length;
  const hasSelection = selected.length > 0;
  const deleteSummary = isDeleteOpen ? summariseDelete() : null;

  // Up and Down move to the same field in the row above or below, as in the
  // Planning grid; leaving the field saves it. The unit picker keeps its
  // arrows for its own options.
  const handleGridKeyDown = (event: React.KeyboardEvent<HTMLTableElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    const target = event.target as HTMLElement;
    // The unit picker and the wrapping notes use their own arrows.
    if (target.closest('.ant-select') || target.matches('textarea')) return;
    const field = target.closest<HTMLElement>('[data-grid-col]');
    const column = field?.dataset.gridCol;
    if (!field || !column) return;
    const fields = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        `[data-grid-col="${column}"]`
      )
    );
    const next =
      fields[fields.indexOf(field) + (event.key === 'ArrowDown' ? 1 : -1)];
    const input = next?.querySelector('input');
    if (!input) return;
    event.preventDefault();
    input.focus();
    input.select();
  };

  // A word beside the name, not a colour, marks an excluded row.
  const renderExcluded = (isExcluded: boolean) =>
    isExcluded ? (
      <span className="flex flex-shrink-0 items-center gap-1 rounded border border-gray-medium bg-white px-1.5 text-sm text-gray-dark">
        <BiHide aria-hidden size={14} />
        {PADDOCKS_EXCLUDED_LABEL}
      </span>
    ) : null;

  // A farm's last row: a new paddock being filled in, or the small Add
  // paddock control (the Tasks grid's Add input), indented under the farm.
  const renderAddPaddockRow = (farm: IFarm) =>
    adding?.kind === 'paddock' && adding.farmId === farm.id ? (
      <DraftRow
        key={`draft-paddock:${farm.id}`}
        kind="paddock"
        namePlaceholder={PADDOCKS_PADDOCK_NAME_PLACEHOLDER}
        requiredMessage={PADDOCK_NAME_REQUIRED_MESSAGE}
        defaultAreaUnit={farm.areaUnit ?? defaultAreaUnit}
        gUnits={gUnits}
        cellClassName={CELL}
        onSave={(values) => addPaddock(farm.id, values)}
        onCancel={() => setAdding(null)}
      />
    ) : (
      <tr key={`add-paddock:${farm.id}`}>
        <td className={CELL} />
        <td colSpan={5} className={`${CELL} stack-full px-2 py-1.5`}>
          <AddNewButton
            type="button"
            text={PADDOCKS_ADD_PADDOCK_LABEL}
            size="sm"
            className="ml-4 gap-1 border border-solid border-gray-argent bg-white px-2 py-1 text-xs font-semibold text-secondary-dark hover:border-gray-argent hover:bg-gray-bright"
            icon={<FaPlus aria-hidden className="text-secondary-dark" size={10} />}
            onClick={() => setAdding({ kind: 'paddock', farmId: farm.id })}
          />
        </td>
      </tr>
    );

  // A sortable column heading; aria-sort tells assistive tech the order.
  // A sortable column heading, drawn as Analysis's (AnalysisGrid SortArrow);
  // aria-sort tells assistive tech the order. Number columns align right.
  const renderSortHeader = (key: PaddockSortKey, label: string) => {
    const isActive = sort.key === key;
    const isNumber = key !== 'name';
    const Arrow =
      isActive && sort.direction === 'desc' ? LuArrowDown : LuArrowUp;
    return (
      <th
        scope="col"
        className={`${HEAD_CELL} ${isNumber ? 'text-right' : ''}`}
        aria-sort={
          isActive
            ? sort.direction === 'asc'
              ? 'ascending'
              : 'descending'
            : 'none'
        }
      >
        <button
          type="button"
          title={PADDOCKS_SORT_TOOLTIP}
          className={`inline-flex w-full items-center rounded hover:text-charcoal focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${isNumber ? 'justify-end' : 'justify-start'}`}
          onClick={() => toggleSort(key)}
        >
          {label}
          <Arrow
            aria-hidden
            size={15}
            strokeWidth={3.5}
            className={`ml-1 shrink-0 ${isActive ? 'text-gray-dark' : 'text-gray-medium'}`}
          />
        </button>
      </th>
    );
  };

  return (
    <div
      ref={containerRef}
      className="flex w-full max-w-[72rem] flex-col gap-3"
    >
      <PlanningUndoNote note={undoNote} />
      {/* A neutral note, sized to its text — the yellow information fill
          is kept for alerts and warnings. */}
      {isOneFarm && scopedFarms[0] && orgId && (
        <p className="flex w-fit max-w-full flex-wrap items-center gap-x-2 rounded bg-secondary-light px-3 py-2 text-sm text-charcoal">
          {PADDOCKS_SCOPED_NOTICE(scopedFarms[0].farmName)}
          <LinkButton
            onClick={() =>
              navigateFarms({ nodeKey: `${ORG_KEY_PREFIX}${orgId}` })
            }
          >
            {PADDOCKS_SHOW_ALL_FARMS_LABEL}
          </LinkButton>
        </p>
      )}

      {/* Find: search, then the labelled Show toggle — Nutrition's
          ControlLabel + Segmented for a small fixed choice. Saving shows in
          the workspace's status label. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="w-64">
          <SearchInput
            value={search}
            autoFocus={false}
            placeholder={PADDOCKS_SEARCH_PLACEHOLDER}
            onChange={setSearch}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-dark">
            {PADDOCKS_STATUS_FILTER_LABEL}
          </span>
          <Segmented<PaddockStatusFilter>
            aria-label={PADDOCKS_STATUS_FILTER_LABEL}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: PADDOCKS_FILTER_ALL_LABEL },
              { value: 'included', label: PADDOCKS_FILTER_INCLUDED_LABEL },
              { value: 'excluded', label: PADDOCKS_FILTER_EXCLUDED_LABEL },
            ]}
          />
        </div>
      </div>

      {/* Act: a hint, or what's ticked, on the left; the bulk actions on the
          right — always shown, so nothing jumps when a row is ticked. */}
      <div className="flex min-h-8 flex-wrap items-center gap-x-4 gap-y-2">
        {hasSelection ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-charcoal">
              {PADDOCKS_SELECTED_COUNT(selected.length)}
            </span>
            {/* Rotation's text-button style (RotationPasteBar). */}
            <button
              type="button"
              className="rounded px-1 text-sm font-semibold text-gray-dark hover:bg-rowHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              onClick={() => setSelection(new Set())}
            >
              {PADDOCKS_CLEAR_SELECTION_LABEL}
            </button>
          </div>
        ) : note ? (
          <p
            role="status"
            className={`text-sm ${note.isError ? 'text-danger-dark' : 'text-charcoal'}`}
          >
            {note.text}
          </p>
        ) : (
          <span className="text-sm text-gray-muted">
            {PADDOCKS_SELECT_HINT}{' '}
            {PADDOCKS_RANGE_HINT_BEFORE}{' '}
            <ShortcutKey variant="subtle">
              {PADDOCKS_RANGE_HINT_KEY}
            </ShortcutKey>{' '}
            {PADDOCKS_RANGE_HINT_AFTER}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="px-3"
          disabled={!hasSelection}
          onClick={() => setExcluded(true)}
        >
          {PADDOCKS_EXCLUDE_LABEL}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="px-3"
          disabled={!hasSelection}
          onClick={() => setExcluded(false)}
        >
          {PADDOCKS_INCLUDE_LABEL}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="px-3"
          disabled={!hasSelection}
          onClick={() => setIsDeleteOpen(true)}
        >
          {PADDOCKS_DELETE_LABEL}
        </Button>
        </div>
      </div>

      {/* No farms at all: the workspace's empty state, as Rotation and
          Nutrition show it, with Add farm. */}
      {scopeLevel === 'org' &&
      !isFiltered &&
      farms.length === 0 &&
      adding?.kind !== 'farm' ? (
        <WorkspaceEmptyState message={PADDOCKS_NO_FARMS_MESSAGE}>
          <AddNewButton
            type="button"
            text={PADDOCKS_ADD_FARM_LABEL}
            size="md"
            className="mx-auto gap-2 border border-solid border-gray-argent bg-white px-2 py-1 text-sm font-semibold text-secondary-dark hover:border-gray-argent hover:bg-gray-bright"
            icon={
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-success">
                <FaPlus aria-hidden className="text-white" size={8} />
              </span>
            }
            onClick={() => setAdding({ kind: 'farm' })}
          />
        </WorkspaceEmptyState>
      ) : (
        <div className="plan-preview-container">
          <table
            className="paddocks-grid stack-table-wide w-full border-collapse border border-gray-medium text-left"
            onKeyDown={handleGridKeyDown}
          >
            <caption className="sr-only">{PADDOCKS_GRID_LABEL}</caption>
            <colgroup>
              <col className="w-10" />
              <col className="w-[16rem]" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-24" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className={`${HEAD_CELL} text-center`}>
                  <Checkbox
                    aria-label={PADDOCKS_SELECT_ALL_LABEL}
                    checked={allSelected}
                    indeterminate={hasSelection && !allSelected}
                    disabled={allKeys.length === 0}
                    onChange={() =>
                      setSelection(allSelected ? new Set() : new Set(allKeys))
                    }
                  />
                </th>
                {renderSortHeader('name', PADDOCKS_NAME_COLUMN_LABEL)}
                {renderSortHeader('area', PADDOCKS_AREA_LABEL)}
                {renderSortHeader('arableArea', PADDOCKS_ARABLE_AREA_LABEL)}
                <th scope="col" className={HEAD_CELL}>
                  {PADDOCKS_UNIT_LABEL}
                </th>
                <th scope="col" className={HEAD_CELL}>
                  {PADDOCKS_NOTES_LABEL}
                </th>
              </tr>
            </thead>
            <tbody>
              {farms.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className={`${CELL} stack-full px-3 py-3 text-sm text-gray-dark`}
                  >
                    {isFiltered
                      ? PADDOCKS_NO_MATCHES_MESSAGE
                      : PADDOCKS_NO_FARMS_MESSAGE}
                  </td>
                </tr>
              )}

              {farms.map((farm) => (
                <React.Fragment key={farm.id}>
                  <tr
                    className={`paddocks-grid-row ${farm.isExcluded ? 'paddocks-grid-row-excluded' : ''}`}
                    onContextMenu={(e) => openMenu(e, farmKey(farm.id))}
                  >
                    <td className={`${FARM_CELL} px-2 text-center`}>
                      <Checkbox
                        aria-label={PADDOCKS_SELECT_ROW_LABEL(farm.farmName)}
                        checked={selection.has(farmKey(farm.id))}
                        onChange={(e) =>
                          toggle(farmKey(farm.id), isShiftClick(e))
                        }
                      />
                    </td>
                    <td className={`${FARM_CELL} stack-primary`}>
                      <div className="flex items-center gap-2 pr-2">
                        <GridTextInput
                          value={farmValues(farm).farmName}
                          column="name"
                          className="min-w-0 flex-1 font-semibold text-charcoal"
                          ariaLabel={PADDOCKS_FIELD_LABEL(
                            PADDOCKS_FARM_NAME_PLACEHOLDER,
                            farm.farmName
                          )}
                          requiredMessage={FARM_NAME_REQUIRED_MESSAGE}
                          onCommit={(farmName) =>
                            editFarm(
                              farm,
                              'farmName',
                              PADDOCKS_NAME_COLUMN_LABEL,
                              farmName
                            )
                          }
                        />
                        {renderExcluded(farm.isExcluded)}
                      </div>
                    </td>
                    <td className={FARM_CELL} data-label={PADDOCKS_AREA_LABEL}>
                      <GridNumberInput
                        column="area"
                        value={farmValues(farm).farmArea}
                        ariaLabel={PADDOCKS_FIELD_LABEL(
                          PADDOCKS_AREA_LABEL,
                          farm.farmName
                        )}
                        onCommit={(farmArea) =>
                          editFarm(
                            farm,
                            'farmArea',
                            PADDOCKS_AREA_LABEL,
                            farmArea
                          )
                        }
                      />
                    </td>
                    {/* Arable area is set per paddock. */}
                    <td className={`${FARM_CELL} stack-empty`} />
                    <td className={FARM_CELL} data-label={PADDOCKS_UNIT_LABEL}>
                      <AreaUnitSelect
                        value={farmValues(farm).areaUnit}
                        gUnits={gUnits}
                        ariaLabel={PADDOCKS_FIELD_LABEL(
                          PADDOCKS_UNIT_LABEL,
                          farm.farmName
                        )}
                        onChange={(areaUnit) =>
                          editFarm(
                            farm,
                            'areaUnit',
                            PADDOCKS_UNIT_LABEL,
                            areaUnit
                          )
                        }
                      />
                    </td>
                    <td
                      className={`${FARM_CELL} stack-full`}
                      data-label={PADDOCKS_NOTES_LABEL}
                    >
                      <GridTextInput
                        value={farmValues(farm).notes ?? ''}
                        column="notes"
                        multiline
                        ariaLabel={PADDOCKS_FIELD_LABEL(
                          PADDOCKS_NOTES_LABEL,
                          farm.farmName
                        )}
                        onCommit={(notes) =>
                          editFarm(
                            farm,
                            'notes',
                            PADDOCKS_NOTES_LABEL,
                            notes || null
                          )
                        }
                      />
                    </td>
                  </tr>

                  {(paddocksByFarm.get(farm.id) ?? []).map(
                    (paddock: IPaddock) => (
                      <tr
                        key={paddock.paddockId}
                        className={`paddocks-grid-row ${paddock.isExcluded ? 'paddocks-grid-row-excluded' : ''} ${paddock.paddockId === selectedPaddockId ? 'paddocks-grid-row-current' : ''}`}
                        aria-current={
                          paddock.paddockId === selectedPaddockId
                            ? 'true'
                            : undefined
                        }
                        title={
                          paddock.paddockId === selectedPaddockId
                            ? PADDOCKS_SELECTED_IN_TREE_LABEL
                            : undefined
                        }
                        onContextMenu={(e) =>
                          openMenu(e, paddockKey(paddock.paddockId))
                        }
                      >
                        <td className={`${CELL} px-2 text-center`}>
                          <Checkbox
                            aria-label={PADDOCKS_SELECT_ROW_LABEL(
                              paddock.paddockName
                            )}
                            checked={selection.has(
                              paddockKey(paddock.paddockId)
                            )}
                            onChange={(e) =>
                              toggle(
                                paddockKey(paddock.paddockId),
                                isShiftClick(e)
                              )
                            }
                          />
                        </td>
                        <td className={`${CELL} stack-primary`}>
                          <div className="flex items-center gap-2 pr-2">
                            <GridTextInput
                              value={paddockValues(paddock).paddockName}
                              column="name"
                              className="paddocks-grid-indent min-w-0 flex-1 text-charcoal"
                              ariaLabel={PADDOCKS_FIELD_LABEL(
                                PADDOCKS_PADDOCK_NAME_PLACEHOLDER,
                                paddock.paddockName
                              )}
                              requiredMessage={PADDOCK_NAME_REQUIRED_MESSAGE}
                              onCommit={(paddockName) =>
                                editPaddock(
                                  farm,
                                  paddock,
                                  'paddockName',
                                  PADDOCKS_NAME_COLUMN_LABEL,
                                  paddockName
                                )
                              }
                            />
                            {renderExcluded(paddock.isExcluded)}
                          </div>
                        </td>
                        <td className={CELL} data-label={PADDOCKS_AREA_LABEL}>
                          <GridNumberInput
                            column="area"
                            value={paddockValues(paddock).paddockArea}
                            ariaLabel={PADDOCKS_FIELD_LABEL(
                              PADDOCKS_AREA_LABEL,
                              paddock.paddockName
                            )}
                            onCommit={(paddockArea) =>
                              editPaddock(
                                farm,
                                paddock,
                                'paddockArea',
                                PADDOCKS_AREA_LABEL,
                                paddockArea
                              )
                            }
                          />
                        </td>
                        <td
                          className={CELL}
                          data-label={PADDOCKS_ARABLE_AREA_LABEL}
                        >
                          <GridNumberInput
                            column="arableArea"
                            value={paddockValues(paddock).arableArea}
                            ariaLabel={PADDOCKS_FIELD_LABEL(
                              PADDOCKS_ARABLE_AREA_LABEL,
                              paddock.paddockName
                            )}
                            onCommit={(arableArea) =>
                              editPaddock(
                                farm,
                                paddock,
                                'arableArea',
                                PADDOCKS_ARABLE_AREA_LABEL,
                                arableArea
                              )
                            }
                          />
                        </td>
                        <td className={CELL} data-label={PADDOCKS_UNIT_LABEL}>
                          <AreaUnitSelect
                            value={paddockValues(paddock).areaUnit}
                            gUnits={gUnits}
                            ariaLabel={PADDOCKS_FIELD_LABEL(
                              PADDOCKS_UNIT_LABEL,
                              paddock.paddockName
                            )}
                            onChange={(areaUnit) =>
                              editPaddock(
                                farm,
                                paddock,
                                'areaUnit',
                                PADDOCKS_UNIT_LABEL,
                                areaUnit
                              )
                            }
                          />
                        </td>
                        <td
                          className={`${CELL} stack-full`}
                          data-label={PADDOCKS_NOTES_LABEL}
                        >
                          <GridTextInput
                            value={paddockValues(paddock).notes ?? ''}
                            column="notes"
                            multiline
                            ariaLabel={PADDOCKS_FIELD_LABEL(
                              PADDOCKS_NOTES_LABEL,
                              paddock.paddockName
                            )}
                            onCommit={(notes) =>
                              editPaddock(
                                farm,
                                paddock,
                                'notes',
                                PADDOCKS_NOTES_LABEL,
                                notes || null
                              )
                            }
                          />
                        </td>
                      </tr>
                    )
                  )}

                  {/* Hidden while searching or filtering, where a new paddock
                      could land out of view. */}
                  {!isFiltered && renderAddPaddockRow(farm)}
                </React.Fragment>
              ))}

              {adding?.kind === 'farm' && (
                <DraftRow
                  kind="farm"
                  namePlaceholder={PADDOCKS_FARM_NAME_PLACEHOLDER}
                  requiredMessage={FARM_NAME_REQUIRED_MESSAGE}
                  defaultAreaUnit={defaultAreaUnit}
                  gUnits={gUnits}
                  cellClassName={FARM_CELL}
                  onSave={addFarm}
                  onCancel={() => setAdding(null)}
                />
              )}

              {/* Add farm closes the grid on a lilac ground, apart from each
                  farm's own Add paddock, with the Tasks grid's filled icon. */}
              {scopeLevel === 'org' && adding?.kind !== 'farm' && (
                <tr className="bg-gray-smoke">
                  <td colSpan={6} className="stack-full px-2 py-2">
                    <AddNewButton
                      type="button"
                      text={PADDOCKS_ADD_FARM_LABEL}
                      size="md"
                      className="gap-2 border border-solid border-gray-argent bg-white px-2 py-1 text-sm font-semibold text-secondary-dark hover:border-gray-argent hover:bg-gray-bright"
                      icon={
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-success">
                          <FaPlus aria-hidden className="text-white" size={8} />
                        </span>
                      }
                      onClick={() => {
                        setSearch('');
                        setStatusFilter('all');
                        setAdding({ kind: 'farm' });
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}


      {menu && hasSelection && (
        <PaddocksContextMenu
          x={menu.x}
          y={menu.y}
          count={selected.length}
          canExclude={selected.some((key) => !excludedByKey.get(key))}
          canInclude={selected.some((key) => excludedByKey.get(key))}
          onExclude={() => setExcluded(true)}
          onInclude={() => setExcluded(false)}
          onDelete={() => setIsDeleteOpen(true)}
          onClose={closeMenu}
        />
      )}

      {deleteSummary && (
        <Modal
          open
          mousePosition={MODAL_FROM_CENTRE}
          title={PADDOCKS_DELETE_TITLE(
            deleteSummary.farmCount,
            deleteSummary.paddockCount
          )}
          onCancel={() => setIsDeleteOpen(false)}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="px-3"
                onClick={() => setIsDeleteOpen(false)}
              >
                {PADDOCKS_CANCEL_LABEL}
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                className="px-3"
                onClick={confirmDelete}
              >
                {PADDOCKS_DELETE_LABEL}
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-2 text-sm text-charcoal">
            <ul className="flex flex-col">
              {deleteSummary.names.slice(0, DELETE_LIST_MAX).map((name, i) => (
                <li key={`${name}-${i}`} className="font-semibold">
                  {name}
                </li>
              ))}
              {deleteSummary.names.length > DELETE_LIST_MAX && (
                <li>
                  {PADDOCKS_MORE_COUNT(
                    deleteSummary.names.length - DELETE_LIST_MAX
                  )}
                </li>
              )}
            </ul>
            {deleteSummary.farmCount > 0 && (
              <p className="text-gray-dark">{PADDOCKS_DELETE_FARMS_NOTE}</p>
            )}
            {deleteSummary.planCount > 0 && (
              <p className="text-danger-dark">
                {PADDOCKS_DELETE_PLANS_WARNING(deleteSummary.planCount)}
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PaddocksView;
